import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Users, FolderOpen, CalendarClock, FileText, Send, Award, XCircle, Percent, Clock } from "lucide-react";

const CARDS = [
  { key: "new_leads_today", label: "New Leads Today", icon: Users, accent: true },
  { key: "total_open_leads", label: "Total Open Leads", icon: FolderOpen },
  { key: "follow_ups_due", label: "Follow-ups Due", icon: CalendarClock },
  { key: "quotes_in_progress", label: "Quotes In Progress", icon: FileText },
  { key: "quotes_sent", label: "Quotes Sent", icon: Send },
  { key: "policies_sold", label: "Policies Sold", icon: Award, accent: true },
  { key: "lost_leads", label: "Lost Leads", icon: XCircle },
  { key: "conversion_rate", label: "Conversion Rate", icon: Percent, suffix: "%" },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [fu, setFu] = useState({ due_today: [], overdue: [], upcoming: [] });
  const nav = useNavigate();
  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/dashboard/followups").then((r) => setFu(r.data));
  }, []);

  return (
    <div className="p-4 sm:p-6">
      <h1 className="font-display text-3xl font-bold mb-1">Dashboard</h1>
      <p className="text-slate-500 mb-6">Your agency at a glance.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {CARDS.map((c) => (
          <div key={c.key} className={`rounded-sm border p-5 ${c.accent ? "bg-[#0B1E36] text-white border-[#0B1E36]" : "bg-white border-slate-200"}`} data-testid={`stat-${c.key}`}>
            <div className="flex items-center justify-between">
              <c.icon size={20} className={c.accent ? "text-[#C5A059]" : "text-slate-400"} />
            </div>
            <p className="font-mono text-3xl font-bold mt-3">{stats ? stats[c.key] : "—"}{c.suffix || ""}</p>
            <p className={`text-xs uppercase tracking-wider mt-1 ${c.accent ? "text-slate-300" : "text-slate-500"}`}>{c.label}</p>
          </div>
        ))}
      </div>

      {stats?.incomplete_quotes > 0 && (
        <div onClick={() => nav("/crm/leads?incomplete=true")} className="mt-4 cursor-pointer bg-[#C5A059]/15 border border-[#C5A059] rounded-sm p-4 flex items-center gap-3" data-testid="incomplete-banner">
          <Clock className="text-[#C5A059]" />
          <span className="font-semibold">{stats.incomplete_quotes} incomplete quote{stats.incomplete_quotes > 1 ? "s" : ""} — don't lose these leads. Follow up now.</span>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        {[["Overdue Follow-ups", fu.overdue, "text-red-600"], ["Due Today", fu.due_today, "text-[#C5A059]"], ["Upcoming", fu.upcoming, "text-slate-600"]].map(([title, items, color]) => (
          <div key={title} className="bg-white border border-slate-200 rounded-sm p-5">
            <h3 className={`font-display text-lg font-bold ${color}`}>{title} <span className="font-mono text-sm">({items.length})</span></h3>
            <div className="mt-3 space-y-2 max-h-64 overflow-auto">
              {items.length === 0 ? <p className="text-sm text-slate-400">Nothing here.</p> :
                items.map((i) => (
                  <button key={i.id} onClick={() => nav(`/crm/leads/${i.id}`)} className="block w-full text-left border border-slate-100 rounded-sm px-3 py-2 hover:bg-slate-50" data-testid={`fu-item-${i.id}`}>
                    <span className="font-semibold text-sm">{i.name || "Unnamed lead"}</span>
                    <span className="block text-xs text-slate-500">{new Date(i.date).toLocaleDateString()} · {i.status}</span>
                  </button>
                ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
